import { useState, useRef, useEffect } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faPlus, faSpinner, faInfoCircle } from '@/fontawesome.ts';
import Modal from '@/components/ui/Modal.tsx';
import NotificationStatsCardCheckbox from './NotificationStatsCardCheckbox';
import RecommendationStatsModal from './RecommendationStatsModal.tsx';
import { APIRoutes } from '@/services/api.routes.ts';
import { handleAPIError, showSuccessToast, showErrorToast } from '@/utils/toasts.ts';
import { toCurrencyCentValue } from '@/utils/helpers.ts';
import type { RecommendationSwingtrade, RecommendationCreateStats } from '@/models';

interface AddSwingTradeAdjustementModalProps {
  isOpen: boolean;
  onClose: () => void;
  swingTrade: RecommendationSwingtrade | null;
  onAdjustmentAdded: (swingTrade: RecommendationSwingtrade) => void;
}

export default function AddSwingTradeAdjustementModal({
  isOpen,
  onClose,
  swingTrade,
  onAdjustmentAdded
}: AddSwingTradeAdjustementModalProps) {
  const [delta, setDelta] = useState<number>(0);
  const [price, setPrice] = useState<number>(0);
  const [sendCommunity, setSendCommunity] = useState(true);
  const [comment, setComment] = useState('');
  const [loading, setLoading] = useState(false);

  // Stats preview state
  const [estimatedStats, setEstimatedStats] = useState<RecommendationCreateStats | null>(null);
  const [statsLoading, setStatsLoading] = useState(false);
  const [statsError, setStatsError] = useState<string | null>(null);
  const [totalUserCount, setTotalUserCount] = useState<number | null>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);

  // Stats modal state
  const [showStatsModal, setShowStatsModal] = useState(false);
  const [createdStats, setCreatedStats] = useState<RecommendationCreateStats | null>(null);
  const [updatedTrade, setUpdatedTrade] = useState<RecommendationSwingtrade | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    if (!swingTrade) return;

    // Validation
    if (delta === 0) {
      showErrorToast('% des Depots darf nicht 0 sein');
      return;
    }

    if (price <= 0) {
      showErrorToast('Preis muss größer als 0 sein');
      return;
    }

    // Calculate new position
    const deltaDecimal = delta / 100; // Convert percentage to 0-1 range
    const newPosition = swingTrade.currentPosition + deltaDecimal;

    // Validate new position
    if (newPosition <= 0) {
      showErrorToast('Die neue Position würde negativ sein. Verwenden Sie "Trade beenden" stattdessen.');
      return;
    }

    if (newPosition > 1) {
      showErrorToast('Die neue Position würde über 100% liegen. Maximal erlaubt ist 100%.');
      return;
    }

    setLoading(true);

    try {
      const requestData = {
        delta:        deltaDecimal,
        price:        toCurrencyCentValue(price),
        sendDiscord:  sendCommunity,
        sendTelegram: sendCommunity,
        sendSCN:      sendCommunity,
        sendPush:     sendCommunity,
        sendMails:    sendCommunity,
        comment:      comment.trim() !== '' ? comment.trim() : null,
      };

      const response = await APIRoutes.addSwingTradeAdjustement(swingTrade.id, requestData, false);
      setUpdatedTrade(response.recommendation);
      setCreatedStats(response.stats);
      setShowStatsModal(true);
    } catch (error) {
      handleAPIError(error, 'Fehler beim Hinzufügen des Einstiegs');
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    if (!loading) {
      setDelta(0);
      setPrice(0);
      setSendCommunity(true);
      setComment('');
      setShowStatsModal(false);
      setCreatedStats(null);
      setUpdatedTrade(null);
      setEstimatedStats(null);
      setStatsError(null);
      onClose();
    }
  };

  const handleStatsModalClose = () => {
    if (updatedTrade !== null) {
      onAdjustmentAdded(updatedTrade);
    }
    showSuccessToast('Einstieg erfolgreich hinzugefügt');
    handleClose();
  };

  // Check if form is valid for fetching stats
  const isFormValid = delta !== 0 && price > 0 && swingTrade !== null;

  // Build request payload for both submit and dryRun
  const buildRequestPayload = () => {
    if (swingTrade === null) return null;

    const deltaDecimal = delta / 100;
    const newPosition = swingTrade.currentPosition + deltaDecimal;

    // Validate new position
    if (newPosition <= 0 || newPosition > 1) {
      return null;
    }

    return {
      delta:        deltaDecimal,
      price:        toCurrencyCentValue(price),
      sendDiscord:  sendCommunity,
      sendTelegram: sendCommunity,
      sendSCN:      sendCommunity,
      sendPush:     sendCommunity,
      sendMails:    sendCommunity,
      comment:      comment.trim() !== '' ? comment.trim() : null,
    };
  };

  // Fetch estimated notification stats in dryRun mode
  const fetchEstimatedStats = async () => {
    if (!isFormValid || swingTrade === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    const requestPayload = buildRequestPayload();
    if (requestPayload === null) {
      setEstimatedStats(null);
      setStatsError(null);
      setStatsLoading(false);
      return;
    }

    setStatsLoading(true);
    setStatsError(null);

    try {
      const response = await APIRoutes.addSwingTradeAdjustement(swingTrade.id, requestPayload, true);
      setEstimatedStats(response.stats);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'Fehler beim Laden der Statistiken';
      setStatsError(errorMessage);
      setEstimatedStats(null);
    } finally {
      setStatsLoading(false);
    }
  };

  // Debounced effect to fetch stats when form changes
  useEffect(() => {
    if (!isFormValid) {
      setEstimatedStats(null);
      setStatsError(null);
      return;
    }

    setStatsLoading(true);

    if (debounceRef.current !== null) {
      clearTimeout(debounceRef.current);
    }

    debounceRef.current = setTimeout(() => {
      fetchEstimatedStats();
    }, 500);

    return () => {
      if (debounceRef.current !== null) {
        clearTimeout(debounceRef.current);
      }
    };
  }, [delta, price, sendCommunity, comment, swingTrade?.id]);

  // Fetch total user count when modal opens
  useEffect(() => {
    if (isOpen) {
      APIRoutes.listUsers({ limit: 1 }).then(response => {
        setTotalUserCount(response.total);
      }).catch(() => {
        setTotalUserCount(null);
      });
    }
  }, [isOpen]);

  // Fetch current symbol price when modal opens
  useEffect(() => {
    if (isOpen && swingTrade !== null && price === 0) {
      APIRoutes.listSymbols({ ticker: swingTrade.symbolTicker, limit: 1 })
        .then(response => {
          const exactMatch = response.symbols.find(s => s.ticker === swingTrade.symbolTicker);
          if (exactMatch?.currentData?.price !== null && exactMatch?.currentData?.price !== undefined) {
            setPrice(exactMatch.currentData.price);
          }
        })
        .catch(() => {
          // Silently ignore errors - user can still enter price manually
        });
    }
  }, [isOpen, swingTrade?.symbolTicker]);

  if (!swingTrade) return null;

  // Calculate what the new position would be
  const deltaDecimal = delta / 100;
  const newPosition = swingTrade.currentPosition + deltaDecimal;

  return (
    <>
    <Modal
      isOpen={isOpen && !showStatsModal}
      onClose={handleClose}
      title="Einstieg hinzufügen"
      icon={<FontAwesomeIcon icon={faPlus} className="w-5 h-5 text-blue-600" />}
      maxWidth="2xl"
      closeDisabled={loading}
    >
      <form onSubmit={handleSubmit} className="p-6 space-y-6">
        {/* Info Banner */}
        <div className="bg-blue-50 dark:bg-blue-900/20 border border-blue-200 dark:border-blue-800 rounded-lg p-4">
          <div className="flex items-start gap-3">
            <FontAwesomeIcon
              icon={faInfoCircle}
              className="w-5 h-5 text-blue-600 dark:text-blue-400 mt-0.5 flex-shrink-0"
            />
            <div className="text-sm text-blue-800 dark:text-blue-200">
              <p className="font-medium mb-1">Depot-Prozentsätze</p>
              <p>
                <strong>Positiv (z.B. +25)</strong>: Kaufen / Position vergrößern<br />
                <strong>Negativ (z.B. -15)</strong>: Verkaufen / Position verkleinern<br />
                Die Position muss zwischen 0% und 100% bleiben.
              </p>
            </div>
          </div>
        </div>

        {/* Current Position Info */}
        <div className="bg-gray-50 dark:bg-gray-800 rounded-lg p-4 space-y-2">
          <div className="flex justify-between text-sm">
            <span className="text-gray-600 dark:text-gray-400">Aktuelle Position:</span>
            <span className="font-medium text-gray-900 dark:text-white">
              {(swingTrade.currentPosition * 100).toFixed(2)}%
            </span>
          </div>
          {delta !== 0 && (
            <div className="flex justify-between text-sm pt-2 border-t border-gray-200 dark:border-gray-700">
              <span className="text-gray-600 dark:text-gray-400">Neue Position:</span>
              <span className={`font-medium ${
                newPosition > 0 && newPosition <= 1
                  ? 'text-green-600 dark:text-green-400'
                  : 'text-red-600 dark:text-red-400'
              }`}>
                {(newPosition * 100).toFixed(2)}%
                {newPosition <= 0 && ' (ungültig)'}
                {newPosition > 1 && ' (ungültig)'}
              </span>
            </div>
          )}
        </div>

        {/* Delta */}
        <div>
          <label htmlFor="delta" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            % des Depots *
          </label>
          <input
            type="number"
            id="delta"
            value={delta || ''}
            onChange={(e) => setDelta(parseFloat(e.target.value) || 0)}
            step="0.01"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="z.B. 25.00 oder -15.00"
            disabled={loading}
            autoFocus
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Positiv = Kaufen, Negativ = Verkaufen (in % des verfügbaren Kapitals)
          </p>
        </div>

        {/* Price */}
        <div>
          <label htmlFor="price" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
            Preis  *
          </label>
          <input
            type="number"
            id="price"
            value={price || ''}
            onChange={(e) => setPrice(parseFloat(e.target.value) || 0)}
            step="0.01"
            min="0"
            className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
            placeholder="100.00"
            disabled={loading}
          />
          <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
            Der Preis zum Zeitpunkt der Anpassung
          </p>
        </div>

        {/* Community Notification */}
        <NotificationStatsCardCheckbox
          checked={sendCommunity}
          onChange={setSendCommunity}
          disabled={loading}
          estimatedStats={estimatedStats}
          statsLoading={statsLoading}
          statsError={statsError}
          totalUserCount={totalUserCount}
        />

        {/* Comment */}
        {sendCommunity && (
          <div>
            <label htmlFor="comment" className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-2">
              Kommentar
            </label>
            <textarea
              id="comment"
              value={comment}
              onChange={(e) => setComment(e.target.value)}
              rows={2}
              className="w-full px-3 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 bg-white dark:bg-gray-700 text-gray-900 dark:text-white text-sm"
              placeholder="Optional: Zusätzlicher Kommentar für die Benachrichtigung"
              disabled={loading}
            />
          </div>
        )}

        <div className="flex justify-end space-x-3 pt-4 border-t border-gray-200 dark:border-gray-700">
          <button
            type="button"
            onClick={handleClose}
            disabled={loading}
            className="px-4 py-2 text-gray-700 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 hover:bg-gray-200 dark:hover:bg-gray-600 rounded-lg transition-colors cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={loading || delta === 0 || price <= 0 || newPosition <= 0 || newPosition > 1}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors font-medium flex items-center gap-2 cursor-pointer disabled:opacity-50 disabled:cursor-not-allowed"
          >
            {loading ? (
              <>
                <FontAwesomeIcon icon={faSpinner} className="w-4 h-4 animate-spin" />
                Hinzufügen...
              </>
            ) : (
              'Einstieg hinzufügen'
            )}
          </button>
        </div>
      </form>
    </Modal>

    {createdStats !== null && (
      <RecommendationStatsModal
        isOpen={showStatsModal}
        onClose={handleStatsModalClose}
        stats={createdStats}
        recommendationType="swing"
        text={`Einstieg erfolgreich hinzugefügt`}
      />
    )}
    </>
  );
}
